import { useMemo, useCallback } from 'react';
import { NavigationItem, navigationConfig } from '../config/navigationConfig';
import { useUserRoles } from './useUserRoles';

export const useFilteredNavigation = () => {
  const { hasAnyRole, hasAllRoles, currentUser, isLoading } = useUserRoles();

  const filterNavigationItems = useCallback((items: NavigationItem[]): NavigationItem[] => {
    return items
      .filter(item => {
        // If no roles are specified, item is always visible
        if (!item.allowedRoles) return true;

        // Check if user has required roles
        const hasPermission = item.requireAll 
          ? hasAllRoles(item.allowedRoles)
          : hasAnyRole(item.allowedRoles);

        return hasPermission;
      })
      .map(item => ({
        ...item,
        // Recursively filter sub-items if they exist
        subItems: item.subItems ? filterNavigationItems(item.subItems) : undefined,
      }))
      // Remove items that have no visible sub-items (if they had sub-items originally)
      .filter(item => {
        // If the original item had sub-items but now has none after filtering, hide the parent
        const originalItem = navigationConfig.find(orig => orig.text === item.text);
        if (originalItem?.subItems && (!item.subItems || item.subItems.length === 0)) {
          return false;
        }
        return true;
      });
  }, [hasAnyRole, hasAllRoles]);

  const filteredNavigationItems = useMemo(() => {
    // Don't filter if user data is still loading
    if (isLoading) return navigationConfig;
    
    return filterNavigationItems(navigationConfig);
  }, [isLoading, filterNavigationItems]);

  return {
    navigationItems: filteredNavigationItems,
    isLoading,
    currentUser,
  };
};